import { alpha, keyframes, type SxProps, type Theme } from "@mui/material/styles";

import { customGradients, customShadows, instagramBrandColor } from "@/theme/customTokens";

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

export const root =
  (variant: "page" | "panel"): SxProps<Theme> =>
  (theme) => ({
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: variant === "page" ? "1.05fr 1fr" : "1fr" },
    gap: theme.spacing(variant === "page" ? 3 : 2.5),
    alignItems: "start",
  });

export const heroCard: SxProps<Theme> = (theme) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 3,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  boxShadow: theme.shadows[3],
  p: { xs: 2.5, md: 3.5 },
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.25),
  "&::before": {
    content: '""',
    position: "absolute",
    insetInlineEnd: -90,
    top: -110,
    width: 260,
    height: 260,
    borderRadius: "50%",
    background: customGradients.instagram,
    opacity: 0.14,
    filter: "blur(10px)",
    pointerEvents: "none",
  },
});

export const logoBubble: SxProps<Theme> = {
  position: "relative",
  width: 64,
  height: 64,
  borderRadius: 2.5,
  background: customGradients.instagram,
  boxShadow: customShadows.instagram,
  color: "common.white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  animation: `${float} 3.2s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

export const badge: SxProps<Theme> = (theme) => ({
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  px: 1.25,
  py: 0.5,
  borderRadius: 5,
  bgcolor: alpha(instagramBrandColor, 0.08),
  color: instagramBrandColor,
  fontSize: "0.71875rem",
  fontWeight: 700,
});

export const title: SxProps<Theme> = {
  position: "relative",
  fontSize: { xs: "1.125rem", md: "1.375rem" },
  fontWeight: 800,
  lineHeight: 1.6,
};

export const description: SxProps<Theme> = {
  position: "relative",
  fontSize: { xs: "0.8125rem", md: "0.875rem" },
  color: "text.secondary",
  lineHeight: 1.9,
};

export const cta: SxProps<Theme> = {
  alignSelf: { xs: "stretch", sm: "flex-start" },
  minHeight: 50,
  px: 3.5,
  color: "common.white",
  background: customGradients.instagram,
  boxShadow: customShadows.instagram,
  "&:hover": { background: customGradients.instagram, filter: "brightness(0.94)" },
  "&.Mui-disabled": {
    background: customGradients.instagram,
    color: "common.white",
    opacity: 0.65,
  },
};

export const secureNote: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: "0.71875rem",
  color: "text.secondary",
  lineHeight: 1.7,
});

export const steps: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  gap: theme.spacing(1.25),
  pt: 2,
  borderTop: 1,
  borderColor: "divider",
});

export const step: SxProps<Theme> = (theme) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: "0.75rem",
  fontWeight: 600,
});

export const stepNumber: SxProps<Theme> = {
  width: 26,
  height: 26,
  flex: "none",
  borderRadius: "50%",
  bgcolor: "primary.main",
  color: "primary.contrastText",
  fontSize: "0.75rem",
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const sideColumn: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
});

export const sectionCard: SxProps<Theme> = (theme) => ({
  borderRadius: 3,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  p: { xs: 2, md: 2.5 },
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
});

export const sectionTitle: SxProps<Theme> = {
  fontSize: "0.875rem",
  fontWeight: 700,
};

export const sectionNote: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const featureGrid: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: theme.spacing(1.25),
});

export const requirement: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: "0.78125rem",
  lineHeight: 1.7,
});
